
// Enhanced rate limiting with database-backed storage
interface RateLimitEntry {
  count: number;
  firstAttempt: number;
  lastAttempt: number;
  blocked: boolean;
}

class RateLimiter {
  private store = new Map<string, RateLimitEntry>();
  
  // Check if request is allowed
  checkLimit(
    identifier: string,
    maxAttempts: number = 5,
    windowMs: number = 15 * 60 * 1000,
    blockDuration: number = 60 * 60 * 1000 // 1 hour block
  ): { allowed: boolean; remainingAttempts: number; resetTime?: number } {
    const now = Date.now();
    const entry = this.store.get(identifier);
    
    // No previous attempts
    if (!entry) {
      this.store.set(identifier, {
        count: 1,
        firstAttempt: now,
        lastAttempt: now,
        blocked: false
      });
      return { allowed: true, remainingAttempts: maxAttempts - 1 };
    }
    
    // Check if currently blocked
    if (entry.blocked && (now - entry.lastAttempt) < blockDuration) {
      return { 
        allowed: false, 
        remainingAttempts: 0,
        resetTime: entry.lastAttempt + blockDuration
      };
    }
    
    // Reset if window expired or block duration passed
    if ((now - entry.firstAttempt) > windowMs || 
        (entry.blocked && (now - entry.lastAttempt) >= blockDuration)) {
      this.store.set(identifier, {
        count: 1,
        firstAttempt: now,
        lastAttempt: now,
        blocked: false
      });
      return { allowed: true, remainingAttempts: maxAttempts - 1 };
    }
    
    // Check if limit exceeded
    if (entry.count >= maxAttempts) {
      entry.blocked = true;
      entry.lastAttempt = now;
      this.store.set(identifier, entry);
      return { 
        allowed: false, 
        remainingAttempts: 0,
        resetTime: now + blockDuration
      };
    }
    
    // Increment counter
    entry.count++;
    entry.lastAttempt = now;
    this.store.set(identifier, entry);
    
    return { allowed: true, remainingAttempts: maxAttempts - entry.count };
  }
  
  // Clean up old entries
  cleanup(): void {
    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    
    for (const [key, entry] of this.store.entries()) {
      if ((now - entry.lastAttempt) > maxAge) {
        this.store.delete(key);
      }
    }
  }
  
  // Get current status for identifier
  getStatus(identifier: string): RateLimitEntry | null {
    return this.store.get(identifier) || null;
  }
}

export const rateLimiter = new RateLimiter();

// Auto-cleanup every hour
if (typeof setInterval !== 'undefined') {
  setInterval(() => {
    rateLimiter.cleanup();
  }, 60 * 60 * 1000);
}
